<template>
	<!-- one Operators audit feed entry: actor, named event, target, the change lines and,
	     behind "Technische Details", the raw before/after. Expansion and the differences-only
	     switch are owned by the list, so one entry is open at a time and the switch sticks -->
	<div class="audit-entry" data-cy="auditEntry">
		<div class="audit-head">
			<b data-cy="auditActor">{{ entry.userEmail }}</b>
			<v-chip size="x-small" data-cy="auditEvent" :color="eventColor">{{ eventLabel }}</v-chip>
			<span v-if="!hideTarget" data-cy="auditTarget">{{ targetName }}</span>
			<span class="audit-time">{{ new Date(entry.createdAt).toLocaleString() }}</span>
		</div>
		<div v-if="lineGroups.length" class="audit-lines">
			<div v-for="group of lineGroups" :key="group.module" class="audit-line-group">
				<div v-for="(line, index) of group.lines" :key="index" class="audit-line" data-cy="auditChangeLine">{{ line.text }}</div>
			</div>
		</div>
		<!-- an entry of an unknown shape: the changed fields and the stored summary, so no row is empty -->
		<template v-if="event === 'other'">
			<div class="audit-fields" v-if="entry.changedFields?.length">
				<v-chip v-for="field of entry.changedFields" :key="field" size="x-small" variant="outlined" class="mr-1" data-cy="auditChangedField">{{ field }}</v-chip>
			</div>
			<div v-if="summary" class="audit-summary">{{ summary }}</div>
		</template>
		<a class="audit-details-toggle" data-cy="auditDetailsToggle" href="#" @click.prevent="$emit('toggle')">
			{{ label('operatorAuditTechnicalDetails', 'Technical details') }}
			<v-icon size="small">{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
		</a>
		<div v-if="expanded" class="audit-diff-wrap">
			<v-switch density="compact" hide-details color="primary" class="audit-diff-toggle" data-cy="auditDiffOnlyToggle"
				:label="$t('text.showDifferencesOnly')"
				:model-value="diffOnly"
				@update:modelValue="$emit('update:diffOnly', !!$event)"
			/>
			<!-- differences-only: deep-diffed changed paths; falls back to the full
			     before/after view when there is nothing comparable to diff -->
			<table v-if="diffOnly && diffGroups.length" class="audit-diff-table">
				<template v-for="group of diffGroups">
					<!-- changes inside one object (peaksolution.role, .username, …) read as one block -->
					<tr v-if="group.rows.length > 1 || group.rows[0].sub" class="diff-group" :key="group.name + '.header'">
						<td colspan="4">{{ group.name }}</td>
					</tr>
					<tr v-for="row of group.rows" :key="group.name + '.' + row.sub" data-cy="auditDiffRow">
						<td class="diff-path">{{ row.sub || group.name }}</td>
						<td class="diff-old"><pre>{{ formatDiffValue(row.oldValue) }}</pre></td>
						<td class="diff-arrow">→</td>
						<td class="diff-new"><pre>{{ formatDiffValue(row.newValue) }}</pre></td>
					</tr>
				</template>
			</table>
			<div v-else class="audit-diff">
				<div v-if="entry.oldData"><div class="field-label">{{ $t('text.before') }}</div><pre>{{ JSON.stringify(entry.oldData, null, 2) }}</pre></div>
				<div v-if="entry.newData"><div class="field-label">{{ $t('text.after') }}</div><pre>{{ JSON.stringify(entry.newData, null, 2) }}</pre></div>
			</div>
		</div>
	</div>
</template>

<script>
import { auditDiffGroups, formatDiffValue } from './operator-audit-diff'
import { changeLineGroups, eventColor, EVENT_FALLBACKS, translatedLabel } from './operator-audit-lines'

export default {
	name: 'OperatorAuditEntry',
	props: {
		// an AuditFeedEntry from OperatorsExecutive.getClientAuditLog
		entry: { type: Object, required: true },
		// resolved by the list (operator list, entry e-mail, sibling entry, id)
		targetName: String,
		// the per-operator history already knows its target
		hideTarget: Boolean,
		expanded: Boolean,
		diffOnly: Boolean,
		// Report Center dimension values ({ regions: [{ id, label }], … }) to name scope ids
		dimensionValues: { type: Object, default: () => ({}) },
		// Peak roles ([{ id, name }]) to name the stored Peaksolution role id
		peakRoles: { type: Array, default: () => [] },
	},
	emits: [ 'toggle', 'update:diffOnly' ],
	computed: {
		event() {
			return this.entry.event ?? 'other'
		},
		eventLabel() {
			return this.label(`operatorAuditEvent_${ this.event }`, EVENT_FALLBACKS[this.event] ?? this.event)
		},
		eventColor() {
			return eventColor(this.event)
		},
		lineGroups() {
			return changeLineGroups(this.entry.changes, { label: this.label, dimensionValues: this.dimensionValues, peakRoles: this.peakRoles })
		},
		summary() {
			const summary = this.entry.changeSummary
			if (!summary) return null
			return summary[this.$store.state.selectedLocale] ?? summary.en ?? Object.values(summary)[0] ?? null
		},
		// only computed while expanded: collapsed entries never pay for the deep diff
		diffGroups() {
			return this.expanded ? auditDiffGroups(this.entry) : []
		},
	},
	methods: {
		formatDiffValue,
		label(key, fallback) {
			return translatedLabel(this, key, fallback)
		},
	},
}
</script>

<style scoped>
.audit-entry { border-bottom: 1px solid #e5e5e5; padding: 10px 4px; }
.audit-head { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.audit-head .audit-time { margin-left: auto; color: #646464; font-size: 10pt; }
.audit-lines { margin-top: 4px; }
.audit-line-group + .audit-line-group { margin-top: 2px; }
.audit-line { font-size: 10pt; overflow-wrap: anywhere; }
.audit-summary { margin-top: 4px; }
.audit-fields { margin-top: 4px; }
.audit-details-toggle { display: inline-flex; align-items: center; margin-top: 4px; font-size: 9pt; color: #646464; text-decoration: none; }
.audit-diff-wrap { margin-top: 8px; }
.audit-diff-toggle { margin-bottom: 4px; }
.audit-diff { display: flex; gap: 12px; }
.audit-diff > div { flex: 1 1 0; min-width: 0; }
.audit-diff pre { background: #f6f6f6; border-radius: 6px; padding: 8px; font-size: 9pt; overflow-x: auto; max-height: 260px; }
.audit-diff-table { width: 100%; border-collapse: collapse; font-size: 9pt; }
.audit-diff-table td { padding: 4px 8px; border-bottom: 1px solid #eee; vertical-align: top; }
.audit-diff-table .diff-path { font-family: monospace; color: #646464; white-space: nowrap; padding-left: 20px; }
.audit-diff-table .diff-group td { font-family: monospace; font-weight: 600; padding-top: 10px; border-bottom: none; }
.audit-diff-table .diff-arrow { color: #646464; }
.audit-diff-table pre { margin: 0; white-space: pre-wrap; word-break: break-word; }
.audit-diff-table .diff-old pre { background: #fdecea; border-radius: 4px; padding: 2px 6px; }
.audit-diff-table .diff-new pre { background: #e6f4ea; border-radius: 4px; padding: 2px 6px; }
.field-label { font-size: 12px; color: #666; padding-bottom: 2px; }
</style>
